// favoritesService.js
import { getFavorites, toggleFavorite } from '../utils/favorites';
import { getPreloaded } from '../utils/preload';
import { __ } from '@wordpress/i18n';

// In-memory cache as final fallback
const templateCache = new Map();

// Storage utility with fallbacks
const storage = {
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        templateCache.set(key, value);
      }
    }
  },

  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      if (item !== null) return JSON.parse(item);
    } catch (e) {}

    try {
      const item = sessionStorage.getItem(key);
      if (item !== null) return JSON.parse(item);
    } catch (e) {}

    return templateCache.get(key);
  },

  remove: (key) => {
    try { localStorage.removeItem(key); } catch (e) {}
    try { sessionStorage.removeItem(key); } catch (e) {}
    templateCache.delete(key);
  },

  clearFavorites: () => {
    storage.remove('favoriteTemplates');
  }
};

// Compare two arrays
function arraysEqual(a, b) {
  return a.length === b.length && a.every((val, index) => val === b[index]);
}

// Load favorite templates (with preloading & caching)
export const loadFavorites = async ({ setTemplates, setLoading, setError }) => {
  try {
    const favs = await getFavorites();

    if (!favs.length) {
      setTemplates([]);
      setLoading(false);
      return;
    }

    const cachedResults = storage.get('favoriteTemplates');

    if (cachedResults && arraysEqual(cachedResults.favs, favs)) {
      setTemplates(cachedResults.templates);
      setLoading(false);
      return;
    }

    const allPreloaded = ['patterns', 'pages', 'kits'].flatMap(getPreloaded);
    const fromCache = allPreloaded.filter((tpl) => favs.includes(tpl.id));
    const foundIds = fromCache.map((tpl) => tpl.id);
    const missingIds = favs.filter((id) => !foundIds.includes(id));

    if (missingIds.length === 0) {
      storage.set('favoriteTemplates', { favs, templates: fromCache });
      setTemplates(fromCache);
      setLoading(false);
      return;
    }

    const apiBase = window?.wpApiSettings?.root || '/wp-json/';
    const url = new URL(`${apiBase}vayu-blocks/v1/manifest`, window.location.origin);
    url.searchParams.set('ids', missingIds.join(','));

    const res = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-WP-Nonce': window?.wpApiSettings?.nonce || '',
      },
    });

    const json = await res.json();
    const fromAPI = Array.isArray(json.templates)
      ? json.templates.filter((tpl) => missingIds.includes(tpl.id))
      : [];

    const combined = [...fromCache, ...fromAPI];
    storage.set('favoriteTemplates', { favs, templates: combined });
    setTemplates(combined);
  } catch (err) {
    console.error('Failed to load favorite templates', err);
    setError(__('Could not load all favorites.', 'vayu-blocks'));
  } finally {
    setLoading(false);
  }
};

// Toggle and update favorite state
export const handleToggleFavorite = async (id, setTemplates) => {
  const updated = await toggleFavorite(id);

  setTemplates((prev) => {
    const newTemplates = prev.filter((tpl) => updated.includes(tpl.id));
    if (newTemplates.length !== prev.length) {
      storage.set('favoriteTemplates', { favs: updated, templates: newTemplates });
    }
    return newTemplates;
  });
};

// Optional: expose manual cache clear
export const clearFavoritesCache = () => storage.clearFavorites();


export const getCachedFavorites = () => {
  const cached = storage.get('favoriteTemplates');
  return Array.isArray(cached?.templates) ? cached.templates : [];
};
